import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, Clock, Phone, ArrowRight, Tag } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Contact";
import { AnimatedBackground } from "@/components/AnimatedBackground";
import { BlogCard } from "@/components/BlogCard";
import {
  BLOG_POSTS,
  getPostBySlug,
  formatHuDate,
  type BlogBlock,
  type BlogPost as BlogPostType,
} from "@/data/blog-posts";
import { SERVICES } from "@/data/services";
import { buildBlogBreadcrumb } from "@/lib/seo";

const RELATED_SERVICES: Record<string, string[]> = {
  "uj-negyoszlopos-emelo-autoszerviz-eger": [
    "futomu-javitas-eger",
    "fekjavitas-eger",
    "autoszerviz-eger",
  ],
  "nyari-guminyomas-es-abroncs-hosegben": [
    "futomu-javitas-eger",
    "autoszerviz-eger",
    "muszaki-vizsga-felkeszites-eger",
  ],
  "klimaszerviz-nyari-utazas-elott": [
    "autoszerviz-eger",
    "fekjavitas-eger",
    "muszaki-vizsga-felkeszites-eger",
  ],
  "hosszu-nyari-autout-felkeszules": [
    "autoszerviz-eger",
    "fekjavitas-eger",
    "futomu-javitas-eger",
  ],
  "mikor-cserejunk-teli-gumira-2026": [
    "gumiszereles-eger",
    "futomu-javitas-eger",
    "autoszerviz-eger",
  ],
};

const SERVICE_ROUTE = {
  "autoszerviz-eger": "/autoszerviz-eger",
  "fekjavitas-eger": "/fekjavitas-eger",
  "futomu-javitas-eger": "/futomu-javitas-eger",
  "motorfelujitas-eger": "/motorfelujitas-eger",
  "muszaki-vizsga-felkeszites-eger": "/muszaki-vizsga-felkeszites-eger",
  "gumiszereles-eger": "/gumiszereles-eger",
  "motorkerekpar-szerviz-eger": "/motorkerekpar-szerviz-eger",
} as const;

const LEGACY_REDIRECTS: Record<string, string> = {
  "nyari-varosi-kozlekedes-autoval": "/blog/hosszu-nyari-autout-felkeszules",
  "hasznalt-auto-vasarlas-elott-mit-ellenorizz": "/blog",
  "muszaki-vizsga-2026-mire-figyeljenek-az-autosok": "/blog",
  "hosszu-elettartamu-autok-titkai": "/blog",
  "uzemanyag-megtakaritas-tippek": "/blog",
};

export const Route = createFileRoute("/blog/$slug")({
  loader: ({ params }) => {
    const legacy = LEGACY_REDIRECTS[params.slug];
    if (legacy) {
      throw redirect({ href: legacy, statusCode: 301 });
    }
    const post = getPostBySlug(params.slug);
    if (!post) throw notFound();
    return { post };
  },
  head: ({ loaderData, params }) => {
    const post = loaderData?.post;
    if (!post) {
      return {
        meta: [{ title: "Nem található | LAKROSZ Autószerviz" }],
      };
    }
    const url = `https://lakroszautoszerviz.com/blog/${params.slug}`;
    const ogImage = `https://lakroszautoszerviz.com${post.image}`;
    return {
      meta: [
        { title: post.seo.metaTitle },
        { name: "description", content: post.seo.metaDescription },
        { property: "og:title", content: post.seo.metaTitle },
        { property: "og:description", content: post.seo.metaDescription },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { property: "og:image", content: ogImage },
        { property: "article:published_time", content: post.date },
        { property: "article:section", content: post.category },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: post.seo.metaTitle },
        { name: "twitter:description", content: post.seo.metaDescription },
        { name: "twitter:image", content: ogImage },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: post.title,
            description: post.excerpt,
            image: ogImage,
            datePublished: post.date,
            dateModified: post.date,
            author: {
              "@type": "Organization",
              name: "LAKROSZ Autószerviz",
            },
            publisher: {
              "@type": "Organization",
              name: "Lakrosz Autószerviz",
              logo: {
                "@type": "ImageObject",
                url: "https://lakroszautoszerviz.com/logo.jpg",
              },
            },
            mainEntityOfPage: url,
          }),
        },
        buildBlogBreadcrumb(post.title, url),
      ],
    };
  },
  notFoundComponent: () => (
    <main className="min-h-screen flex items-center justify-center px-6">
      <div className="text-center">
        <h1 className="font-display text-5xl mb-4">A cikk nem található</h1>
        <Link to="/blog" className="text-primary hover:underline">
          Vissza a blogra
        </Link>
      </div>
    </main>
  ),
  component: BlogPost,
});

function Block({ block }: { block: BlogBlock }) {
  switch (block.type) {
    case "h2":
      return (
        <h2 className="font-display text-3xl md:text-4xl mt-12 mb-4 leading-tight">
          {block.text}
        </h2>
      );
    case "h3":
      return (
        <h3 className="font-display text-xl md:text-2xl mt-8 mb-3 text-primary">
          {block.text}
        </h3>
      );
    case "p":
      return (
        <p
          className="text-base md:text-lg leading-relaxed text-foreground/90 mb-5 [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-4 hover:[&_a]:text-primary-glow"
          dangerouslySetInnerHTML={{ __html: block.text }}
        />
      );
    case "ul":
      return (
        <ul className="space-y-2 my-5 pl-1">
          {block.items.map((it, i) => (
            <li
              key={i}
              className="relative pl-6 text-foreground/90 leading-relaxed"
            >
              <span className="absolute left-0 top-2 w-2 h-2 rounded-full bg-primary" />
              {it}
            </li>
          ))}
        </ul>
      );
    case "table":
      return (
        <div className="my-8 rounded-2xl border border-border bg-surface overflow-hidden">
          {block.title && (
            <div className="px-5 py-3 border-b border-border text-xs uppercase tracking-[0.25em] text-primary">
              ▸ {block.title}
            </div>
          )}
          <dl className="divide-y divide-border">
            {block.rows.map(([k, v], i) => (
              <div
                key={i}
                className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4 px-5 py-3"
              >
                <dt className="text-sm font-semibold text-foreground sm:w-56 sm:shrink-0">
                  {k}
                </dt>
                <dd className="text-sm text-muted-foreground leading-relaxed break-words">
                  {v}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      );
    case "callout":
      return (
        <aside className="my-8 p-6 rounded-2xl border border-primary/40 bg-primary/5">
          {block.title && (
            <div className="text-xs uppercase tracking-[0.25em] text-primary mb-2">
              ▸ {block.title}
            </div>
          )}
          <p
            className="text-foreground/90 leading-relaxed [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-4"
            dangerouslySetInnerHTML={{ __html: block.text }}
          />
        </aside>
      );
    case "cta":
      return (
        <div className="my-10 flex flex-wrap gap-3">
          <a
            href={block.href}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-primary to-primary-glow text-primary-foreground font-semibold glow"
          >
            <Phone className="w-4 h-4" />
            {block.label}
          </a>
          {block.secondaryHref && (
            <a
              href={block.secondaryHref}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-border hover:border-primary text-sm font-semibold transition-colors"
            >
              {block.secondaryLabel}
              <ArrowRight className="w-4 h-4" />
            </a>
          )}
        </div>
      );
  }
}

function BlogPost() {
  const { post } = Route.useLoaderData() as { post: BlogPostType };
  const related = BLOG_POSTS.filter(
    (p) => p.slug !== post.slug && p.category === post.category,
  ).slice(0, 3);
  const fillers = BLOG_POSTS.filter(
    (p) => p.slug !== post.slug && !related.includes(p),
  );
  const relatedFinal = [...related, ...fillers].slice(0, 3);
  const relatedServices = (RELATED_SERVICES[post.slug] ?? [])
    .map((slug) => SERVICES.find((s) => s.slug === slug))
    .filter((s): s is NonNullable<typeof s> => Boolean(s));

  return (
    <main className="relative min-h-screen">
      <AnimatedBackground />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-10 px-6 lg:px-10">
        <div className="relative max-w-4xl mx-auto">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Vissza a bloghoz
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex flex-wrap items-center gap-3 mb-5 text-xs uppercase tracking-widest">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 border border-primary/30 text-primary">
                <Tag className="w-3 h-3" />
                {post.category}
              </span>
              <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                <Calendar className="w-3.5 h-3.5" />
                <time dateTime={post.date}>{formatHuDate(post.date)}</time>
              </span>
              <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                <Clock className="w-3.5 h-3.5" />
                {post.readMinutes} perc olvasás
              </span>
            </div>

            <h1 className="font-display text-4xl md:text-6xl lg:text-7xl leading-[1] mb-6">
              {post.title}
            </h1>

            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-3xl">
              {post.excerpt}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Hero image */}
      <section className="relative px-6 lg:px-10 mb-12">
        <div className="relative max-w-5xl mx-auto">
          <div className="relative aspect-[16/9] rounded-3xl overflow-hidden border border-border">
            <img
              src={post.image}
              alt={post.imageAlt}
              width={1280}
              height={720}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover"
            />
          </div>
          {post.imageCaption && (
            <figcaption className="mt-3 text-sm text-muted-foreground text-center max-w-3xl mx-auto">
              {post.imageCaption}
            </figcaption>
          )}
        </div>
      </section>

      {/* Content */}
      <article className="relative px-6 lg:px-10 pb-20">
        <div className="relative max-w-3xl mx-auto">
          {post.content.map((block, i) => (
            <Block key={i} block={block} />
          ))}

          {/* CTA */}
          <div className="mt-16 p-8 md:p-10 rounded-3xl bg-surface border border-border relative overflow-hidden">
            <div
              className="absolute inset-0 opacity-50 pointer-events-none"
              style={{ background: "var(--gradient-radial)" }}
            />
            <div className="relative">
              <div className="text-xs uppercase tracking-[0.3em] text-primary mb-3">
                ▸ Időpontkérés
              </div>
              <h3 className="font-display text-3xl md:text-4xl mb-3">
                Beszéljünk az autódról.
              </h3>
              <p className="text-muted-foreground mb-6 max-w-xl">
                Ha bármelyik fent leírt téma érinti az autódat, hívj minket
                bizalommal — időpontot egyeztetünk, és átnézzük, mire van valóban
                szükség.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="tel:+36304787619"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-primary to-primary-glow text-primary-foreground font-semibold glow"
                >
                  <Phone className="w-4 h-4" />
                  +36 30 478 7619
                </a>
                <Link
                  to="/"
                  hash="contact"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-border hover:border-primary text-sm font-semibold transition-colors"
                >
                  Üzenetet írok
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </article>

      {/* Kapcsolódó szolgáltatások */}
      {relatedServices.length > 0 && (
        <section className="relative px-6 lg:px-10 pb-20">
          <div className="relative max-w-5xl mx-auto">
            <div className="text-xs uppercase tracking-[0.3em] text-primary mb-3">
              ▸ Kapcsolódó szolgáltatások
            </div>
            <h2 className="font-display text-3xl md:text-4xl mb-3">
              Ezekben tudunk segíteni Egerben.
            </h2>
            <p className="text-muted-foreground mb-8 max-w-2xl">
              Ha a fenti témakör érint, nézd meg azokat a szolgáltatásainkat is, amelyek közvetlenül kapcsolódnak hozzá. Egy helyen, garanciával, átlátható árazással dolgozunk.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {relatedServices.map((s) => (
                <Link
                  key={s.slug}
                  to={SERVICE_ROUTE[s.slug]}
                  className="group p-5 rounded-2xl bg-surface border border-border hover:border-primary/60 hover:bg-surface-elevated transition-all"
                >
                  <div className="font-semibold text-lg mb-1 group-hover:text-primary transition-colors">
                    {s.cardTitle}
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">{s.cardDesc}</p>
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
                    Részletek <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}


      {/* Related */}
      {relatedFinal.length > 0 && (
        <section className="relative px-6 lg:px-10 pb-32">
          <div className="relative max-w-7xl mx-auto">
            <div className="flex items-end justify-between mb-8">
              <div>
                <div className="text-xs uppercase tracking-[0.3em] text-primary mb-3">
                  ▸ További cikkek
                </div>
                <h2 className="font-display text-3xl md:text-4xl">
                  Olvasd el ezeket is.
                </h2>
              </div>
              <Link
                to="/blog"
                className="hidden md:inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                Összes cikk
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {relatedFinal.map((p, i) => (
                <BlogCard key={p.slug} post={p} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </main>
  );
}
